'use client'
import React from 'react';
import { motion } from 'motion/react';
import { Check } from 'lucide-react';
import { TCompaniesConfig } from '@/app/config';

const ImplementationProcess=({config}:{config:TCompaniesConfig["ImplementationProcess"]}) => {
 

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

 

  const stepHoverVariants = {
    hover: {
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

 
  return (
    <motion.section 
      className="py-20 bg-white"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={containerVariants}
    >
      <div className="max-w-5xl mx-auto px-8">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          variants={{
            hidden: { opacity: 0, y: 50 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.8,
                ease: "easeOut"
              }
            }
          }}
        >
          <motion.h2 
            className="text-4xl font-bold text-gray-900 mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
           {config.title}
          </motion.h2>
          <motion.p 
            className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          >
           {config.description}
          </motion.p>
        </motion.div>

        {/* Process Steps */}
        <div className="relative">
          {/* Connecting Line */}
          <motion.div 
            className="absolute left-8 top-12 bottom-12 w-0.5 bg-yellow-300 origin-top"
            variants={{
              hidden: { scaleY: 0, opacity: 0 },
              visible: {
                scaleY: 1,
                opacity: 1,
                transition: {
                  duration: 2,
                  ease: "easeInOut",
                  delay: 0.5
                }
              }
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          />

          {/* Animated pulse effect on timeline */}
          <motion.div
            className="absolute left-7 top-12 w-2 h-2 bg-yellow-400 rounded-full"
            variants={ {
              animate: {
                scale: [1, 1.1, 1],
                opacity: [0.3, 0.6, 0.3],
                transition: {
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }
              }
            }}
            animate="animate"
            style={{ transformOrigin: "center" }}
          />
          
          <motion.div 
            className="space-y-12"
            variants={containerVariants}
          >
            {config.processSteps.map((step, index) => (
              <motion.div 
                key={index} 
                className="relative flex items-start"
                variants={{
                  hidden: { 
                    opacity: 0, 
                    x: -100,
                    scale: 0.8
                  },
                  visible: {
                    opacity: 1,
                    x: 0,
                    scale: 1,
                    transition: {
                      duration: 0.7,
                      ease: "easeOut"
                    }
                  }
                }}
                whileHover="hover"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                {...stepHoverVariants}
              >
                {/* Step Number Circle */}
                <motion.div 
                  className="relative z-10 w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer"
                  variants={ {
                    hidden: { 
                      scale: 0,
                      rotate: -180,
                      opacity: 0
                    },
                    visible: {
                      scale: 1,
                      rotate: 0,
                      opacity: 1,
                      transition: {
                        type: "spring",
                        stiffness: 200,
                        damping: 15,
                        delay: 0.2
                      }
                    },
                    hover: {
                      scale: 1.1,
                      rotate: [0, -5, 5, 0],
                      boxShadow: "0 10px 25px rgba(251, 191, 36, 0.4)",
                      transition: {
                        duration: 0.4,
                        ease: "easeInOut"
                      }
                    }
                  }}
                  whileHover="hover"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                >
                  <motion.span 
                    className="text-white text-xl font-bold"
                    variants={ {
                      hidden: { scale: 0, opacity: 0 },
                      visible: {
                        scale: 1,
                        opacity: 1,
                        transition: {
                          type: "spring",
                          stiffness: 300,
                          damping: 20,
                          delay: 0.4
                        }
                      },
                      hover: {
                        scale: 1.2,
                        transition: {
                          duration: 0.2
                        }
                      }
                    }}
                    whileHover="hover"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2 + 0.1 }}
                  >
                    {step.number}
                  </motion.span>
                  
                  {/* Ripple effect on hover */}
                  <motion.div
                    className="absolute inset-0 bg-yellow-400 rounded-full opacity-0"
                    whileHover={{
                      scale: [1, 1.5],
                      opacity: [0, 0.3, 0],
                    }}
                    transition={{ duration: 0.6 }}
                  />
                </motion.div>

                {/* Step Content */}
                <motion.div 
                  className="ml-8 flex-1 grid grid-cols-1 lg:grid-cols-3 gap-8"
                  variants={ {
                    hidden: { opacity: 0, y: 30 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.6,
                        ease: "easeOut",
                        delay: 0.4
                      }
                    }
                  }}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 + 0.2 }}
                >
                  {/* Left Column - Title and Description */}
                  <div className="lg:col-span-2">
                    <motion.h3 
                      className="text-2xl font-bold text-gray-900 mb-3 cursor-pointer"
                      variants={ {
                        hidden: { opacity: 0, y: 20 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.5,
                            ease: "easeOut",
                            delay: 0.3
                          }
                        },
                        hover: {
                          color: "#eab308",
                          scale: 1.05,
                          transition: {
                            duration: 0.2
                          }
                        }
                      }}
                      whileHover="hover"
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.2 + 0.3 }}
                    >
                      {step.title}
                    </motion.h3>
                    
                    <motion.p 
                      className="text-gray-600 leading-relaxed mb-4"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.2 + 0.4, ease: "easeOut" }}
                    >
                      {step.description}
                    </motion.p>
                    
                    <motion.div 
                      className="inline-block bg-gray-100 text-gray-700 text-sm px-3 py-1 rounded-full cursor-pointer"
                      variants={{
                        hidden: { opacity: 0, scale: 0 },
                        visible: {
                          opacity: 1,
                          scale: 1,
                          transition: {
                            type: "spring",
                            stiffness: 300,
                            damping: 20,
                            delay: 0.5
                          }
                        },
                        hover: {
                          scale: 1.05,
                          backgroundColor: "#eab308",
                          color: "#ffffff",
                          transition: {
                            duration: 0.2
                          }
                        }
                      }}
                      whileHover="hover"
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.2 + 0.5 }}
                    >
                      {step.duration}
                    </motion.div>
                  </div>

                  {/* Right Column - Key Deliverables */}
                  <motion.div 
                    className="bg-gray-50 rounded-2xl p-6 cursor-pointer"
                    variants={{
                      hidden: { 
                        opacity: 0, 
                        x: 50,
                        scale: 0.9
                      },
                      visible: {
                        opacity: 1,
                        x: 0,
                        scale: 1,
                        transition: {
                          duration: 0.6,
                          ease: "easeOut",
                          delay: 0.6
                        }
                      },
                      hover: {
                        scale: 1.02,
                        backgroundColor: "#f8fafc",
                        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                        transition: {
                          duration: 0.3,
                          ease: "easeOut"
                        }
                      }
                    }}
                    whileHover="hover"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2 + 0.6 }}
                  >
                    <motion.h4 
                      className="text-lg font-semibold text-gray-900 mb-4"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.2 + 0.7, ease: "easeOut" }}
                    >
                      Key Deliverables:
                    </motion.h4>
                    
                    <motion.div 
                      className="space-y-3"
                      variants={containerVariants}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                    >
                      {step.deliverables.map((deliverable, deliverableIndex) => (
                        <motion.div 
                          key={deliverableIndex} 
                          className="flex items-center space-x-2"
                          variants={{
                            hidden: { opacity: 0, x: -20 },
                            visible: {
                              opacity: 1,
                              x: 0,
                              transition: {
                                duration: 0.4,
                                ease: "easeOut"
                              }
                            },
                            hover: {
                              x: 5,
                              transition: {
                                duration: 0.2,
                                ease: "easeOut"
                              }
                            }
                          }}
                          whileHover="hover"
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.2 + deliverableIndex * 0.1 + 0.8 }}
                        >
                          <motion.div
                            variants={ {
                              hidden: { scale: 0, rotate: -90 },
                              visible: {
                                scale: 1,
                                rotate: 0,
                                transition: {
                                  type: "spring",
                                  stiffness: 400,
                                  damping: 25
                                }
                              },
                              hover: {
                                scale: 1.2,
                                rotate: 360,
                                color: "#22c55e",
                                transition: {
                                  duration: 0.3
                                }
                              }
                            }}
                            whileHover="hover"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.2 + deliverableIndex * 0.1 + 0.9 }}
                          >
                            <Check className="w-4 h-4 text-green-500" />
                          </motion.div>
                          <motion.span 
                            className="text-sm text-gray-600"
                            whileHover={{ 
                              color: "#16a34a",
                              fontWeight: 500
                            }}
                            transition={{ duration: 0.2 }}
                          >
                            {deliverable}
                          </motion.span>
                        </motion.div>
                      ))}
                    </motion.div>
                  </motion.div>
                </motion.div>

                {/* Background glow effect on hover */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-yellow-50 to-transparent rounded-2xl opacity-0 -z-10"
                  whileHover={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.div>
            ))}
          </motion.div>

          {/* Floating completion indicator */}
          <motion.div
            className="absolute left-6 -bottom-8 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center"
            initial={{ scale: 0, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 3, type: "spring", stiffness: 200 }}
          >
            <Check className="w-2 h-2 text-white" />
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default ImplementationProcess;